// API Users management page — create, activate, regenerate, revoke API keys.
// Route: /t/:slug/admin/api-users

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { apiUsersApi, generateAPIKey, hashAPIKey } from '@/lib/api/apiUsers'
import type { APIUser } from '@/lib/api/apiUsers'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/lib/hooks/use-toast'
import { Plus, Key, RotateCcw, Ban, Copy, Check, FileJson, Download, AlertTriangle } from 'lucide-react'
import { buildCredentialsFile, downloadCredentialsFile } from '@/lib/utils/credentials-file'

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 border-green-200',
  unactivated: 'bg-amber-100 text-amber-800 border-amber-200',
  revoked: 'bg-red-100 text-red-800 border-red-200',
}

export function ApiUsersPage() {
  const { slug, tenantId } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const client = apiUsersApi(api)

  const [createOpen, setCreateOpen] = useState(false)
  const [keyDialogOpen, setKeyDialogOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<APIUser | null>(null)
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null)
  const [connectionKey, setConnectionKey] = useState<string | null>(null)
  const [activatingUserId, setActivatingUserId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [downloaded, setDownloaded] = useState(false)

  const {
    data: users,
    error,
    isLoading,
  } = useQuery({
    queryKey: queryKeys.apiUsers.all(slug),
    queryFn: () => client.list(),
  })

  // --- Create ---
  const [newName, setNewName] = useState('')
  const createMutation = useMutation({
    mutationFn: () => client.create({ display_name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiUsers.all(slug) })
      setCreateOpen(false)
      setNewName('')
      toast({ title: 'API user created', description: 'Activate the key to start using it.' })
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to create API user', description: err.message, variant: 'destructive' })
    },
  })

  // --- Activate / Regenerate ---
  async function handleActivate(userId: string, isRegenerate: boolean) {
    setActivatingUserId(userId)
    try {
      const key = await generateAPIKey()
      const hash = await hashAPIKey(key)
      const prefix = key.substring(0, 8)
      const fn = isRegenerate ? client.regenerate : client.activate
      await fn(userId, { key_hash: hash, key_prefix: prefix })

      // Build and auto-download credentials file
      const creds = buildCredentialsFile(tenantId, key)
      downloadCredentialsFile(creds, prefix)

      setPlaintextKey(key)
      setConnectionKey(creds.connection_key)
      setKeyDialogOpen(true)
      setCopied(false)
      setDownloaded(true)
      queryClient.invalidateQueries({ queryKey: queryKeys.apiUsers.all(slug) })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      toast({ title: 'Failed to activate key', description: msg, variant: 'destructive' })
    } finally {
      setActivatingUserId(null)
    }
  }

  // --- Revoke ---
  const revokeMutation = useMutation({
    mutationFn: (userId: string) => client.revoke(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiUsers.all(slug) })
      setRevokeTarget(null)
      toast({ title: 'API key revoked' })
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to revoke key', description: err.message, variant: 'destructive' })
    },
  })

  // --- Copy ---
  async function handleCopy() {
    if (!connectionKey) return
    await navigator.clipboard.writeText(connectionKey)
    setCopied(true)
    toast({ title: 'Connection key copied to clipboard' })
    setTimeout(() => setCopied(false), 2000)
  }

  function handleKeyDialogClose() {
    setKeyDialogOpen(false)
    setPlaintextKey(null)
    setConnectionKey(null)
    setCopied(false)
  }

  // --- Render ---
  if (isLoading) return <TableSkeleton rows={4} />
  if (error) return <ErrorState message="Failed to load API users" technicalDetail={error.message} />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">API Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage API keys for AI tools and integrations.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create API User
        </Button>
      </div>

      {users && users.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <Key className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">No API users yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Create an API user to enable AI tools to access your risk data.
          </p>
          <Button className="mt-4" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create API User
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Key Prefix</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Roles</th>
                <th className="px-4 py-3 text-left font-medium">Last Used</th>
                <th className="px-4 py-3 text-left font-medium">Created</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{u.display_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {u.key_prefix ? `${u.key_prefix}...` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={statusColors[u.key_status] ?? ''}>
                      {u.key_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <APIUserRoleCell user={u} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {u.last_used_at ? new Date(u.last_used_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {u.key_status === 'unactivated' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActivate(u.id, false)}
                          disabled={activatingUserId === u.id}
                        >
                          <Key className="mr-1 h-3 w-3" />
                          {activatingUserId === u.id ? 'Generating...' : 'Activate'}
                        </Button>
                      )}
                      {u.key_status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleActivate(u.id, true)}
                            disabled={activatingUserId === u.id}
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />
                            {activatingUserId === u.id ? 'Generating...' : 'Regenerate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setRevokeTarget(u)}
                          >
                            <Ban className="mr-1 h-3 w-3" />
                            Revoke
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API User</DialogTitle>
            <DialogDescription>
              Give the API user a descriptive name (e.g. &quot;AI Read-Only&quot; or &quot;Integration Bot&quot;).
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Display name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) createMutation.mutate()
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Display Dialog (credentials file) */}
      <Dialog open={keyDialogOpen} onOpenChange={handleKeyDialogClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              The credentials file has been downloaded. Copy the connection key below
              for use with LLM tools.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {downloaded && (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3">
                <Download className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <p className="text-sm text-green-800">
                  Credentials file downloaded. Attach this file when connecting AI tools
                  like ChatGPT or Claude.
                </p>
              </div>
            )}

            <div>
              <p className="mb-1.5 text-sm font-medium">Connection key</p>
              <div className="flex items-center gap-2 rounded-md bg-muted p-3">
                <code className="flex-1 break-all text-xs">{connectionKey}</code>
                <Button variant="ghost" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Paste this into any LLM tool that asks for a RiskQuilt connection key.
              </p>
            </div>

            {plaintextKey && (
              <div>
                <p className="mb-1.5 text-sm font-medium">Raw API key</p>
                <code className="block break-all rounded-md bg-muted p-3 text-xs">{plaintextKey}</code>
              </div>
            )}

            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">
                Store the credentials file securely. It cannot be re-downloaded.
                If lost, regenerate the key to get a new file.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleKeyDialogClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              This will permanently disable the API key for{' '}
              <strong>{revokeTarget?.display_name}</strong>. Any tools using this key will
              immediately lose access. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// --- Helper components ---

function APIUserRoleCell({ user }: { user: APIUser }) {
  const hasOwnership = user.ownership_roles.length > 0
  const hasRoles = user.role_names.length > 0

  if (!hasOwnership && !hasRoles) {
    return <span className="text-muted-foreground">None</span>
  }

  return (
    <div className="flex flex-col gap-0.5">
      {hasOwnership && (
        <>
          {user.ownership_roles.map((name) => (
            <span key={name} className="text-xs font-medium text-amber-700">
              {name}
            </span>
          ))}
          {hasRoles && <div className="my-0.5 border-t border-border" />}
        </>
      )}
      {hasRoles &&
        user.role_names.map((name) => (
          <span key={name} className="text-xs text-muted-foreground">
            {name}
          </span>
        ))}
    </div>
  )
}
